import React, { useEffect, useState } from "react";
import styles from "../../css/shared/MultiSelect.module.scss";
import sprite from "../../assets/icons/carets-arrows.svg";
import { PropTypes } from "prop-types";
import { isEmptyVal } from "../../helpers/utils_types";
import MultiSelectDropdown from "./MultiSelectDropdown";

const advancedSearch = (val, options) => {
	val = val?.toLowerCase();

	return options.filter((option) => {
		if (
			option.toLowerCase().startsWith(val) ||
			option.toLowerCase().includes(val)
		) {
			// matches
			return option;
		} else {
			return null;
		}
	});
};

const getPlaceholder = (placeholder, selections = [], options = []) => {
	if (!placeholder) {
		return `${selections?.length ?? 0} of ${options?.length} selected`;
	} else {
		return placeholder;
	}
};

const MultiSelect = ({
	placeholder,
	handleMultiSelect,
	initialSelections = [],
	disabledOptions = [],
	options = [],
	customOption = null,
	customStyles = {},
	dropdownStyles = {},
}) => {
	const [selections, setSelections] = useState([...initialSelections]);
	const [showOptions, setShowOptions] = useState(false);
	const [clonedOptions, setClonedOptions] = useState([...options]);
	const [searchVal, setSearchVal] = useState("");

	const handleSelect = (option) => {
		if (selections.includes(option)) {
			return setSelections([...selections.filter((x) => x !== option)]);
		}
		return setSelections([...selections, option]);
	};

	const handleSelectAll = () => {
		if (selections.length === options.length) {
			return setSelections([]);
		}
		return setSelections([...options]);
	};

	const handleSearch = (e) => {
		const { value } = e.target;
		setSearchVal(value);
		return searchOptions(value);
	};

	const searchOptions = (val) => {
		if (isEmptyVal(val)) {
			return setClonedOptions([...options]);
		}
		return setClonedOptions([...advancedSearch(val, options)]);
	};

	const clearSelections = () => {
		setSelections([]);
		handleMultiSelect([]);
	};

	const saveSelections = () => {
		setShowOptions(false);
	};

	const clearSearch = () => {
		setSearchVal("");
		searchOptions("");
	};

	// used to send "selections" to the parent via "handleMultiSelect"
	useEffect(() => {
		let isMounted = true;
		if (!isMounted) {
			return;
		}
		handleMultiSelect(selections);
		return () => {
			isMounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selections]);

	return (
		<div className={styles.MultiSelect} style={customStyles}>
			<div
				className={styles.MultiSelect_input}
				onClick={showOptions ? null : () => setShowOptions(true)}
				style={customStyles}
			>
				<span className={styles.MultiSelect_input_placeholder}>
					{getPlaceholder(placeholder, selections, options)}
				</span>
				<svg
					className={styles.MultiSelect_input_downCaret}
					onClick={() => setShowOptions(true)}
				>
					<use xlinkHref={`${sprite}#icon-caret-down`}></use>
				</svg>
			</div>
			{showOptions && (
				<MultiSelectDropdown
					customOption={customOption}
					searchVal={searchVal}
					disabledOptions={disabledOptions}
					options={clonedOptions}
					selections={selections}
					isAllSelected={selections.length === options.length}
					handleSelect={handleSelect}
					handleSelectAll={handleSelectAll}
					handleSearch={handleSearch}
					saveSelections={saveSelections}
					clearSelections={clearSelections}
					clearSearch={clearSearch}
					closeMenuOptions={() => setShowOptions(false)}
					dropdownStyles={dropdownStyles}
				/>
			)}
		</div>
	);
};

export default MultiSelect;

MultiSelect.defaultProps = {
	options: [],
};
MultiSelect.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	).isRequired,
	handleMultiSelect: PropTypes.func.isRequired, // handler from parent component; keeps "selections" in-sync
};
